import { useMutation } from "@tanstack/react-query";
import {
  createPlayerGoal,
  deletePlayerGoal,
  updatePlayerGoal,
  type ApiError,
} from "@/lib/api";
import { invalidateScope } from "@/lib/queryClient";
import type {
  CreatePlayerGoalRequest,
  PlayerGoal,
  UpdatePlayerGoalRequest,
} from "@/lib/types";

/**
 * Player-goal mutation hooks (M6a).
 *
 * M6a ships the goal create/edit MODAL — the goal CARD render +
 * the per-player goal LIST are M7 (they live inside Player
 * Development; legacy `_renderGoalCard` is only rendered there).
 * So there's no `useCoachGoalsList` query in M6a — only the
 * mutations the modal needs. A `getPlayerGoals` API verb exists
 * for M7 to build the list query on.
 *
 * `useCoachGoalMutations` bundles create / update / delete; each
 * `invalidateScope`s so any goal-reading surface (M7's Player
 * Development, once it lands) refetches.
 */

export type CoachGoalMutations = {
  createGoal: (
    body: CreatePlayerGoalRequest,
  ) => Promise<{ goal: PlayerGoal }>;
  updateGoal: (
    id: number,
    body: UpdatePlayerGoalRequest,
  ) => Promise<{ goal: PlayerGoal }>;
  deleteGoal: (id: number) => Promise<{ ok: true }>;
  isPending: boolean;
  error: ApiError | null;
};

export function useCoachGoalMutations(): CoachGoalMutations {
  const create = useMutation({
    mutationFn: createPlayerGoal,
    onSuccess: () => invalidateScope(),
  });
  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: UpdatePlayerGoalRequest;
    }) => updatePlayerGoal(id, body),
    onSuccess: () => invalidateScope(),
  });
  const remove = useMutation({
    mutationFn: deletePlayerGoal,
    onSuccess: () => invalidateScope(),
  });

  const all = [create, update, remove];
  return {
    createGoal: (body) => create.mutateAsync(body),
    updateGoal: (id, body) => update.mutateAsync({ id, body }),
    deleteGoal: (id) => remove.mutateAsync(id),
    isPending: all.some((m) => m.isPending),
    error: (all.find((m) => m.error)?.error as ApiError | undefined) ?? null,
  };
}
